import { Box, createStyles, Group } from '@mantine/core';
import { forwardRef } from 'react';
import { FilterOperator } from '../types';
import { FilterTargetLabel } from './FilterTargetLabel';
import type { OperatorMenuProps } from './OperatorMenu';
import { OperatorMenuReadOnly } from './OperatorMenuReadOnly';

const useStyles = createStyles((theme) => ({
	root: {
		border: `1px solid ${theme.other.getColor('border/primary/default')}`,
		backgroundColor: theme.other.getColor('fill/primary/default'),
		borderRadius: theme.radius.sm,
		padding: `0 0 0 ${theme.spacing.sm}`,
		gap: 0,

		button: {
			outline: 'none !important',
		},
	},
	detailedRoot: {
		gap: 0,
	},
	detailedLabel: {
		paddingRight: theme.spacing['2xs'],
	},
	operatorMenu: {
		padding: `${theme.spacing['5xs']} ${theme.spacing['2xs']}`,
		lineHeight: theme.spacing.lg,
	},
	readOnlyTarget: {
		backgroundColor: theme.other.getColor('fill/primary/default'),
		padding: `${theme.spacing['5xs']} ${theme.spacing.md} ${theme.spacing['5xs']} 0`,
		borderTopRightRadius: theme.radius.sm,
		borderBottomRightRadius: theme.radius.sm,
	},
	detailedValue: {
		fontSize: theme.fontSizes.xs,
		color: theme.other.getColor('text/primary/default'),
		lineHeight: theme.spacing.lg,
	},
}));

export interface FilterTargetDetailedReadOnlyProps {
	label: string;
	valueLabel: React.ReactNode | null;
	operatorMenuProps: Omit<OperatorMenuProps, 'className'>;
}

export const FilterTargetDetailedReadOnly = forwardRef<
	HTMLDivElement,
	FilterTargetDetailedReadOnlyProps
>(
	(
		{ valueLabel, label, operatorMenuProps }: FilterTargetDetailedReadOnlyProps,
		ref
	) => {
		const { classes } = useStyles();

		const canShowTarget = operatorMenuProps?.selected
			? ![FilterOperator.isSet, FilterOperator.isNotSet].includes(
					operatorMenuProps.selected
				)
			: true;

		return (
			<Group
				ref={ref}
				data-testid="filter-target-detailed-readonly"
				className={classes.root}
			>
				<Group className={classes.detailedRoot}>
					<FilterTargetLabel
						label={label}
						showChevron={false}
						className={classes.detailedLabel}
					/>
					<OperatorMenuReadOnly
						label={operatorMenuProps.label}
						className={classes.operatorMenu}
					/>
					{canShowTarget && (
						<Box className={classes.readOnlyTarget}>
							<Group spacing="2xs">
								<div className={classes.detailedValue}>
									{valueLabel ?? '(no selection)'}
								</div>
							</Group>
						</Box>
					)}
				</Group>
			</Group>
		);
	}
);
FilterTargetDetailedReadOnly.displayName = 'FilterTargetDetailedReadOnly';
